import { SchemaUpdate } from "src/api/flowVersionQueries";
import { useUpdateFlowTags } from "src/api/queries";
import { FlowTagsTypeEnum } from "src/clients/flow-api";
import { toastActions } from "src/design-system/Toast/utils";
import { useAuthoringContext } from "src/router/routerContextHooks";
import type { CaseSchemaEditorProps } from "src/schema/schemaEditor/CaseSchemaEditor";
import { isCaseSchema } from "src/schema/schemaEditor/utils";
import { logger } from "src/utils/logger";

export const useUpdateSchemaWithFlowTags = ({
  mutateSchema,
}: {
  mutateSchema: CaseSchemaEditorProps["mutateSchema"];
}) => {
  const { flow, version: currentVersion } = useAuthoringContext();
  const updateFlowTags = useUpdateFlowTags(flow.id);

  return async (schema: SchemaUpdate) => {
    const existingFlowTag = flow.tags?.type;
    const isAddingTemplate = schema.schema.properties.case;
    const flowHasVersionsWithCaseInputSchema = flow.versions.some(
      (version) =>
        version.id !== currentVersion?.id &&
        version.input_schema &&
        isCaseSchema(version.input_schema),
    );

    const shouldUpdateFlowTag =
      isAddingTemplate || !flowHasVersionsWithCaseInputSchema;

    if (shouldUpdateFlowTag) {
      try {
        await updateFlowTags.mutateAsync(
          isAddingTemplate
            ? {
                type: FlowTagsTypeEnum.CASE_FLOW,
              }
            : {},
        );
      } catch (error) {
        logger.error("Error updating flow tags:", error);

        toastActions.failure({
          title: "Unable to select Case template",
          description: "Please refresh the page and try again.",
        });

        // Do not mutate the schema if the flow tag update fails
        // let user retry manually
        return;
      }
    }

    try {
      await mutateSchema(schema);
    } catch (error) {
      // If schema mutation fails and we updated the flow tag, try to revert it
      if (shouldUpdateFlowTag) {
        try {
          await updateFlowTags.mutateAsync({
            type: existingFlowTag,
          });
        } catch (revertError) {
          logger.error("Error reverting flow tag:", revertError);
        }
      }
      // Re-throw the original schema mutation error
      throw error;
    }
  };
};
